/* eslint-disable react/jsx-one-expression-per-line,react/jsx-indent */
import React from 'react';
import { Helmet } from 'react-helmet';
import SmartCarousel from '../components/SmartCarousel';
import impulseRefImage from '../assets/images/impulse/impulse-ref.png';
import impulseImage1 from '../assets/images/impulse/impulse-1.jpg';
import impulseImage2 from '../assets/images/impulse/impulse-2.jpg';
import impulseImage3 from '../assets/images/impulse/impulse-3.jpg';
import impulseImage4 from '../assets/images/impulse/impulse-4.jpg';
import impulseImage5 from '../assets/images/impulse/impulse-5.jpg';
import impulseImage6 from '../assets/images/impulse/impulse-6.jpg';
import impulseImage7 from '../assets/images/impulse/impulse-7.png';
import impulseImage8 from '../assets/images/impulse/impulse-8.png';
import impulseImage9 from '../assets/images/impulse/impulse-9.jpg';
import impulseImage10 from '../assets/images/impulse/impulse-10.jpg';
import impulseImage11 from '../assets/images/impulse/impulse-11.jpg';
import impulseImage12 from '../assets/images/impulse/impulse-12.jpg';
import impulseImage13 from '../assets/images/impulse/impulse-13.jpg';
import impulseImage14 from '../assets/images/impulse/impulse-14.png';
import impulseImage15 from '../assets/images/impulse/impulse-15.jpg';
import impulseImage16 from '../assets/images/impulse/impulse-16.jpg';
import impulseImage17 from '../assets/images/impulse/impulse-17.jpg';
import impulseImage18 from '../assets/images/impulse/impulse-18.jpg';
import impulseImage19 from '../assets/images/impulse/impulse-19.jpg';
import PoweringImageContainer from '../components/PoweringImageContainer';
import PoweringImage from '../components/PoweringImage';
import DetailsHeaderText from '../components/DetailsHeaderText';
import DetailsText from '../components/DetailsText';
import Category from '../components/Category';
import ImportantText from '../components/ImportantText';
import DetailsList from '../components/DetailsList';
import TextWithImageSection from '../components/TextWithImageSection';
import Text from '../components/Text';
import SideIframeVideo from '../components/SideIframeVideo';
import SectionTitle from '../components/SectionTitle';
import ImpulseDetailsLinkSection from './ImpulseDetailsLinkSection';
import Paragraph from '../components/Paragraph';

const ImpulseDetailsContent = () => (
    <>
        <Helmet>
            <title>Electric Bus Operations – Driving innovation through the IMPULSE project - realCity.io</title>
            <meta
                name="description"
                content="IMPULSE, supported by EIT Urban Mobility, enhances electric bus operations with data-driven fleet management. Discover innovative solutions for energy efficiency, real-time monitoring, and sustainable urban mobility."
            />
            <meta
                name="keywords"
                content="Electric bus fleet management, sustainable urban mobility, urban mobility, e-bus management"
            />
        </Helmet>

        <DetailsHeaderText
            category="Stories"
            title="IMPULSE: Driving Innovation for Electric Bus Operations in Public Transport"
        />
        <TextWithImageSection
            text={(
                <>
                    <Text>
                        The IMPULSE (Integrated Management of e-buses Power Utilization to Lead Sustainability and
                        Electrification) project, supported by EIT Urban Mobility and co-funded by the European Union, was
                        carried out to enhance realCity&apos;s traffic management software platform by introducing technical
                        functionalities tailored for electric bus operations. Through two pilots in Lisbon, Portugal and
                        Istanbul, Turkey, capabilities in data collection, processing, and operational decision-making were
                        demonstrated to support the future of sustainable urban mobility.
                    </Text>
                    <ImpulseDetailsLinkSection />
                </>
            )}
            image={(
                <SideIframeVideo
                    src="https://www.youtube.com/embed/ArIe6AZZDks"
                    title="realCity | Impulse+ project"
                    variant="left"
                />
            )}
            alignment="image-right"
        />
        <DetailsText>
            <SectionTitle>Key outcomes:</SectionTitle>
            <DetailsList>
                <li>
                    Enhanced Traffic Management Platform: Integrating electric bus operation support, improving overall
                    fleet efficiency and sustainability.
                </li>
                <li>
                    Innovative Data Collection Methods: Developing scalable approaches for collecting vehicle data,
                    ranging from API integration to on-board units (OBUs) for manual or automated data input.
                </li>
                <li>
                Improved Operational Insights: Extending realCity&apos;s analytics tools to include energy consumption,
                    battery status, and predictive capabilities for optimized fleet operations.
                </li>
                <li>
                    Advanced Prediction and Support Tools: Creating new backend components, such as the range predictor
                    engine and enhanced real-time dispatch tools, providing actionable insights and real-time
                    decision-making support.
                </li>
            </DetailsList>
            <SectionTitle>Technical Features Developed:</SectionTitle>
            <DetailsList>
                <li>
                    Data Collection: Multiple methods, including APIs, CAN hardware integration, and manual data input
                    through our on-board unit software realCity | drive were supported
                </li>
                <li>
                    Data Processing and Storage: realCity | replay was enhanced for historical data analysis, energy
                    consumption tracking, and report generation.
                </li>
                <li>
                    Predictions and Operations Support: realCity's new range predictor service and realCity | core were
                    used to process real-time information.
                </li>
                <li>
                    Fleet operation Optimization: New overview tools and alerts for dispatchers in realCity | dispatch
                    were created to prevent service disruptions caused by premature battery depletion.
                </li>
            </DetailsList>
            <SectionTitle>Pilot cases:</SectionTitle>
            <ol>
                <li>
                    <DetailsList subtitle="Lisbon, Portugal">
                        <li>
                            Data integration through various APIs provided by public transport operator, CARRIS.
                        </li>
                        <li>
                            Energy-related patterns were identified, and widgets were introduced to display average
                            consumption, battery behaviour, and block-level trends.
                        </li>
                    </DetailsList>
                </li>
                <li>
                    <DetailsList subtitle="Princes Islands, Turkey">
                        <li>
                            Manual data collection challenges were addressed through an OBU interface that allowed
                            drivers to input key operational information.
                        </li>
                        <li>
                            Predictive tools were adapted to maintain reliability even where direct vehicle data were
                            limited.
                        </li>
                    </DetailsList>
                </li>
            </ol>
            <Paragraph>
                After the success of the original IMPULSE project, the work was extended under the IMPULSE+ project,
                initiated and supported by EIT Urban Mobility and co-funded by the European Union. The expanded pilot in
                Lisbon was designed to accommodate a wider variety of electric vehicles, making operations smarter, more
                predictable, and more efficient for transport providers.
            </Paragraph>
            <Paragraph>
                The developments were built on the foundations created through IMPULSE. Improved energy-consumption and
                battery-health tracking, real-time operational widgets, and analytical reports that revealed operational
                patterns to support better planning.
                The platform was extended to support mixed fleets by integrating Carris’ newly deployed 14 electric
                buses, ensuring that all vehicles are integrated into one unified system regardless of model or data
                source.
            </Paragraph>
            <SectionTitle>How do we achieve this?</SectionTitle>
            <ImportantText>Unified support for all electric bus types</ImportantText>
            <Paragraph>
                All e-bus models were handled within the same platform, with consistent tracking, standardized data
                processing, and the same AI-powered range predictions and alerts available across the entire fleet.
            </Paragraph>
            <ImportantText>Charger data integrated into daily operations</ImportantText>
            <Paragraph>
                Charging information was incorporated directly into dispatch tools offering live charging status,
                estimated completion times, and automatic notifications when charging anomalies occur. This enabled
                CARRIS to prevent range-related service disruptions before they happen.
            </Paragraph>
            <ImportantText>Enhancements based on IMPULSE insights</ImportantText>
            <Paragraph>
                Prediction accuracy was refined, dashboard clarity was improved, and operational support features were
                expanded to better reflect real-life needs and dispatcher workflows.
            </Paragraph>
            <Paragraph>
                IMPULSE+ reinforced realCity’s commitment to building smarter, more sustainable electric bus operations.
                By broadening predictive intelligence, improving data availability, and addressing real-world challenges
                in mixed electric fleets, the project supported Carris to reduce energy waste, prevent disruptions, and
                improve overall efficiency in an increasingly electrified public transport environment.
            </Paragraph>
            <ImpulseDetailsLinkSection />
        </DetailsText>
        <PoweringImageContainer>
            <PoweringImage logo="impulse" gapSize="large" />
            <PoweringImage logo="eit" gapSize="large" />
        </PoweringImageContainer>
        <SmartCarousel
            images={[
                impulseRefImage,
                impulseImage1,
                impulseImage2,
                impulseImage3,
                impulseImage4,
                impulseImage5,
                impulseImage6,
                impulseImage7,
                impulseImage8,
                impulseImage9,
                impulseImage10,
                impulseImage11,
                impulseImage12,
                impulseImage13,
                impulseImage14,
                impulseImage15,
                impulseImage16,
                impulseImage17,
                impulseImage18,
                impulseImage19,
            ]}
        />
        <Category standalone>Partners</Category>
        <PoweringImageContainer>
            <PoweringImage logo="carnet" />
            <PoweringImage logo="realCity" />
            <PoweringImage logo="kadikoy" />
            <PoweringImage logo="carris" />
        </PoweringImageContainer>
    </>
);

export default ImpulseDetailsContent;
